import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardContent } from './components/ui/card';
import { Input } from './components/ui/input';
import { Button } from './components/ui/button';
import { Alert, AlertDescription } from './components/ui/alert';

function App() {
  const [parameters, setParameters] = useState({});
  const [localParameters, setLocalParameters] = useState({});
  const [error, setError] = useState('');
  const [token, setToken] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [balances, setBalances] = useState({});
  const [hedgesData, setHedgesData] = useState({});
  const [wsConnected, setWsConnected] = useState(false);

  const API_URL = 'https://api.fultonproperties.co.za';

  // WebSocket connections for balances and hedges
  useEffect(() => {
    if (!token) return;

    console.log("Attempting WebSocket connections...");
    const balancesWs = new WebSocket('wss://api.fultonproperties.co.za/ws/balances');
    const hedgesWs = new WebSocket('wss://api.fultonproperties.co.za/ws/hedges');

    // Add periodic ping to keep connections alive
    const pingInterval = setInterval(() => {
      if (balancesWs.readyState === WebSocket.OPEN) {
        balancesWs.send("ping");
      }
      if (hedgesWs.readyState === WebSocket.OPEN) {
        hedgesWs.send("ping");
      }
    }, 30000);

    // Helper function to handle WebSocket messages
    const handleWebSocketMessage = (event, setter) => {
      try {
        let data;
        if (typeof event.data === 'string') {
          data = JSON.parse(event.data);
        } else {
          data = event.data;
        }

        if (typeof data === 'object' && data !== null) {
          setter(prevData => ({
            ...prevData,
            ...data
          }));
        } else {
          console.warn('Received data is not in expected format:', data);
        }
      } catch (error) {
        console.error('Error processing WebSocket data:', error);
      }
    };

    // Balances WebSocket handlers
    balancesWs.onopen = () => {
      console.log('Balances WebSocket Connected');
      setWsConnected(true);
    };

    balancesWs.onmessage = (event) => {
      handleWebSocketMessage(event, setBalances);
    };

    balancesWs.onerror = (error) => {
      console.error('Balances WebSocket error:', error);
      setWsConnected(false);
    };

    balancesWs.onclose = () => {
      console.log('Balances WebSocket Disconnected');
      setWsConnected(false);
    };

    // Hedges WebSocket handlers
    hedgesWs.onopen = () => {
      console.log('Hedges WebSocket Connected');
    };

    hedgesWs.onmessage = (event) => {
      handleWebSocketMessage(event, setHedgesData);
    };

    hedgesWs.onerror = (error) => {
      console.error('Hedges WebSocket error:', error);
    };

    hedgesWs.onclose = () => {
      console.log('Hedges WebSocket Disconnected');
    };

    // Cleanup: close both WebSockets and clear the ping interval
    return () => {
      console.log("Cleaning up WebSocket connections...");
      clearInterval(pingInterval);
      balancesWs.close();
      hedgesWs.close();
    };
  }, [token]);

  const login = async () => {
    try {
      setIsLoading(true);
      console.log('Starting login process...');
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      console.log('Sending login request to:', `${API_URL}/token`);
      console.log('Login credentials:', { username, password });
      
      const response = await fetch(`${API_URL}/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ 
          username: username,
          password: password
        }),
        signal: controller.signal
      }).catch(error => {
        console.error('Fetch error:', error);
        if (error.name === 'AbortError') {
          throw new Error('Login request timed out');
        }
        throw error;
      });
      
      clearTimeout(timeoutId);
      
      console.log('Response received:', response.status);
      
      const responseText = await response.text();
      console.log('Raw response:', responseText);
      
      if (!response.ok) {
        throw new Error(`Login failed with status: ${response.status}`);
      }
      
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (e) {
        console.error('JSON parse error:', e);
        throw new Error('Invalid response format');
      }
      
      console.log('Login successful, token received');
      
      if (!data.access_token) {
        throw new Error('No access token in response');
      }
      
      setToken(data.access_token);
      setUsername('');
      setPassword('');
      setError('');
      
    } catch (err) {
      console.error('Login error details:', err);
      setError(`Login failed: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchParameters = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/parameters`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch parameters');
      }
      
      const data = await response.json();
      setParameters(data);
      setLocalParameters(data);
    } catch (err) {
      setError('Failed to load parameters');
    }
  }, [token]);

  const saveChanges = async () => {
    setIsSaving(true);
    setError('');
    try {
      for (const [key, value] of Object.entries(localParameters)) {
        if (value !== parameters[key]) {
          const response = await fetch(`${API_URL}/update-parameter`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ key, value })
          });
          
          if (!response.ok) {
            throw new Error(`Failed to update ${key}`);
          }
        }
      }
      await fetchParameters();
      setError('');
    } catch (err) {
      setError('Failed to save changes: ' + err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleParameterChange = (key, value) => {
    setLocalParameters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  useEffect(() => {
    if (token) {
      fetchParameters();
    }
  }, [token, fetchParameters]);

  if (!token) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 flex flex-col items-center justify-center p-4">
        <div className="w-full max-w-md">
          <h1 className="text-3xl font-bold text-center text-white mb-8">
            Mattercap Coinbase System
          </h1>
          <Card className="w-full bg-white/95 backdrop-blur-sm shadow-xl">
            <CardHeader>
              <h2 className="text-2xl font-semibold text-center text-gray-800">Login</h2>
            </CardHeader>
            <CardContent>
              {error && (
                <Alert variant="destructive" className="mb-4">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              <div className="space-y-4">
                <div>
                  <Input
                    type="text"
                    placeholder="Username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <Input
                    type="password"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <Button 
                  onClick={login} 
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-lg transition-colors"
                  disabled={isLoading}
                >
                  {isLoading ? 'Logging in...' : 'Login'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const hasChanges = JSON.stringify(parameters) !== JSON.stringify(localParameters);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 p-4">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold text-center text-white mb-8">
          Mattercap Coinbase System
        </h1>
        <Card className="bg-white/95 backdrop-blur-sm shadow-xl">
          <CardHeader>
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-semibold text-gray-800">Trading Parameters</h2>
              <div className="flex gap-2">
                {hasChanges && (
                  <Button 
                    onClick={saveChanges}
                    className="bg-green-600 hover:bg-green-700 text-white"
                    disabled={isSaving}
                  >
                    {isSaving ? 'Saving...' : 'Save Changes'}
                  </Button>
                )}
                <Button 
                  variant="outline" 
                  onClick={() => setToken('')}
                  className="bg-red-50 hover:bg-red-100 text-red-600 border-red-200"
                >
                  Logout
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {error && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* Live Balances Display */}
            <div className="mb-6 p-4 bg-blue-50 rounded-lg">
              <h3 className="text-xl font-semibold mb-4">Live Balances</h3>
              <div className="text-sm text-gray-500 mb-2">
                WebSocket Status: {wsConnected ? 
                  <span className="text-green-500">Connected</span> : 
                  <span className="text-red-500">Disconnected</span>
                }
              </div>
              <div className="grid grid-cols-2 gap-4">
                {Object.entries(balances).map(([currency, value]) => (
                  <div key={currency} className="flex justify-between items-center p-2 bg-white rounded shadow">
                    <span className="font-medium">{currency}:</span>
                    <span className="font-mono">{Math.round(Number(value))}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Hedges Data Display */}
            <div className="mb-6 p-4 bg-green-50 rounded-lg">
              <h3 className="text-xl font-semibold mb-4">Hedges Information</h3>
              <div className="grid grid-cols-2 gap-4">
                {Object.entries(hedgesData).map(([key, value]) => (
                  <div key={key} className="flex justify-between items-center p-2 bg-white rounded shadow">
                    <span className="font-medium">{key}:</span>
                    <span className="font-mono">{value}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Parameters Grid */}
            <div className="grid gap-4">
              {Object.entries(parameters).map(([key, value]) => (
                <div key={key} className="flex items-center gap-4 p-2 rounded-lg hover:bg-gray-50">
                  <label className="w-48 font-medium text-gray-700">{key}</label>
                  <Input
                    type="text"
                    value={localParameters[key] ?? ''}
                    onChange={(e) => handleParameterChange(key, e.target.value)}
                    className="max-w-[200px] p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default App;